<app-spinner-service></app-spinner-service>

<div class="modal-content" *ngIf="socio" [class.modal-content--loading]="isLoading">
  <div class="modal-header">
    <div>
      <h2>Editar Pró-Labore</h2>
      <p>{{ socio.nmTrab }}</p>
    </div>
  </div>

  <div class="info-box">
    <strong>Atenção à competência</strong>
    <p>
      A competência selecionada representa o mês de referência do pró-labore. O sistema só considera esse valor
      quando o envio mensal do eSocial ocorrer para a mesma competência.
    </p>
    <p *ngIf="competenciaPreview">
      Exemplo: ao salvar a competência <strong>{{ competenciaPreview }}</strong>, esse ajuste só será usado
      quando o envio mensal considerar esse mesmo mês de referência.
    </p>
  </div>

  <form [formGroup]="form" class="form-grid">
    <section class="form-section">
      <div class="section-header">
        <h3>Configuração do Pró-Labore</h3>
        <p>Defina se o sócio vai retirar pró-labore nesta competência.</p>
      </div>

      <div class="form-row form-row--three">
        <div class="form-field">
          <label for="vaiRetirarProLabore">Vai retirar pró-labore? <span class="required">*</span></label>
          <p-dropdown
            inputId="vaiRetirarProLabore"
            formControlName="vaiRetirarProLabore"
            [options]="simNaoOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione"
            (onChange)="onVaiRetirarChange($event.value)">
          </p-dropdown>
          <small class="field-help">Ao selecionar "Não", os valores financeiros vinculados serão limpos.</small>
        </div>

        <div class="form-field" *ngIf="form.get('vaiRetirarProLabore')?.value">
          <label for="competencia">Competência <span class="required">*</span></label>
          <p-calendar
            inputId="competencia"
            formControlName="competencia"
            [view]="'month'"
            dateFormat="mm/yy"
            [showIcon]="true"
            [minDate]="minCompetencia"
            [readonlyInput]="true"
            (onSelect)="onCompetenciaChange($event)">
          </p-calendar>
          <small class="field-help">Mês de referência considerado para o envio mensal ao eSocial.</small>
        </div>
      </div>
    </section>

    <section class="form-section" *ngIf="form.get('vaiRetirarProLabore')?.value">
      <div class="section-header">
        <h3>Dados do Pró-Labore</h3>
        <p>Informe o valor bruto e as características principais da retirada mensal.</p>
      </div>

      <div class="form-row form-row--four">
        <div class="form-field form-field--wide">
          <label for="proLaboreMensal">Pró-labore mensal <span class="required">*</span></label>
          <div class="inline-field">
            <p-inputNumber
              inputId="proLaboreMensal"
              formControlName="proLaboreMensal"
              mode="currency"
              currency="BRL"
              locale="pt-BR"
              [min]="0"
              (onBlur)="onProLaboreMensalBlur()"
              [disabled]="!(form.get('vaiRetirarProLabore')?.value)">
            </p-inputNumber>

            <button
              type="button"
              pButton
              icon="pi pi-calculator"
              class="p-button-outlined"
              [disabled]="!(form.get('vaiRetirarProLabore')?.value)"
              (click)="recalcularProLabore()">
            </button>
          </div>
          <small class="field-help">Valor bruto mensal usado para recalcular INSS, IRRF e valor líquido.</small>
        </div>

        <div class="form-field">
          <label for="participacaoSocietaria">Participação societária / cotas <span class="required">*</span></label>
          <p-inputNumber
            inputId="participacaoSocietaria"
            formControlName="participacaoSocietaria"
            [min]="0"
            [max]="100"
            [disabled]="!(form.get('vaiRetirarProLabore')?.value)">
          </p-inputNumber>
          <small class="field-help">Percentual de participação do sócio no capital social.</small>
        </div>

        <div class="form-field">
          <label for="duploVinculo">Possui duplo vínculo? <span class="required">*</span></label>
          <p-dropdown
            inputId="duploVinculo"
            formControlName="duploVinculo"
            [options]="simNaoOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione"
            [disabled]="!(form.get('vaiRetirarProLabore')?.value)">
          </p-dropdown>
          <small class="field-help">Indica se o sócio possui vínculo de trabalho em outra empresa.</small>
        </div>

        <div class="form-field">
          <label for="diaPagamentoProLabore">Dia de pagamento <span class="required">*</span></label>
          <p-dropdown
            inputId="diaPagamentoProLabore"
            formControlName="diaPagamentoProLabore"
            [options]="diasPagamentoOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Selecione"
            [disabled]="!(form.get('vaiRetirarProLabore')?.value)">
          </p-dropdown>
          <small class="field-help">Dia do mês em que o pagamento do pró-labore costuma acontecer.</small>
        </div>
      </div>
    </section>

    <section class="form-section" *ngIf="form.get('vaiRetirarProLabore')?.value">
      <div class="section-header">
        <h3>Valores Calculados</h3>
        <p>Esses campos refletem o cálculo automático com base no valor mensal informado.</p>
      </div>

      <div class="form-row form-row--three">
        <div class="form-field form-field--calculated">
          <label for="inssSobreProLabore">INSS sobre pró-labore <span class="required">*</span></label>
          <span class="field-badge">
            <i class="pi pi-calculator"></i>
            Calculado automaticamente
          </span>
          <p-inputNumber
            inputId="inssSobreProLabore"
            formControlName="inssSobreProLabore"
            mode="currency"
            currency="BRL"
            locale="pt-BR">
          </p-inputNumber>
          <small class="field-help">Desconto previdenciário estimado a partir do pró-labore mensal. Este campo é informativo e não pode ser alterado manualmente.</small>
        </div>

        <div class="form-field form-field--calculated">
          <label for="irrfSobreProLabore">IRRF sobre pró-labore <span class="required">*</span></label>
          <span class="field-badge">
            <i class="pi pi-calculator"></i>
            Calculado automaticamente
          </span>
          <p-inputNumber
            inputId="irrfSobreProLabore"
            formControlName="irrfSobreProLabore"
            mode="currency"
            currency="BRL"
            locale="pt-BR">
          </p-inputNumber>
          <small class="field-help">Valor estimado do imposto de renda retido na fonte. Este campo é informativo e não pode ser alterado manualmente.</small>
        </div>

        <div class="form-field form-field--calculated">
          <label for="valorLiquidoProLabore">Valor líquido <span class="required">*</span></label>
          <span class="field-badge">
            <i class="pi pi-calculator"></i>
            Calculado automaticamente
          </span>
          <p-inputNumber
            inputId="valorLiquidoProLabore"
            formControlName="valorLiquidoProLabore"
            mode="currency"
            currency="BRL"
            locale="pt-BR">
          </p-inputNumber>
          <small class="field-help">Valor final após os descontos automáticos calculados pelo sistema. Este campo é informativo e não pode ser alterado manualmente.</small>
        </div>
      </div>
    </section>
  </form>

  <div class="modal-actions">
    <button type="button" pButton label="Cancelar" class="p-button-secondary" (click)="fechar()"></button>
    <button type="button" pButton label="Salvar" [loading]="isSaving" [disabled]="isSaveDisabled" (click)="salvar()"></button>
  </div>
</div>
